/**
 * V53.2: 좌석배치도 조회 Hook
 *
 * V53.2 변경사항 (빈 화면 버그 수정):
 * - isLoading 초기값을 enabled 기반으로 설정 (첫 렌더부터 LoadingState)
 * - enabled 변경 시 isLoading 상태 동기화
 *
 * V52.15: requestId 기반 stale fetch 차단 + 콘솔 로그 체계화
 *
 * getSeatMap Cloud Function을 호출하여
 * 회사별 좌석배치도 데이터를 조회합니다.
 *
 * @module shared/hooks/useSeatMap
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  CompanyCode,
  SeatMapVariant,
  SeatMapDocument,
  GetSeatMapRequest,
  GetSeatMapResponse,
} from '../types/seatMap';

// ============================================
// V52.15: 디버그 로그 헬퍼
// ============================================

const isDev = process.env.NODE_ENV === 'development';

function logFetch(
  type: 'start' | 'success' | 'stale_ignored' | 'error',
  data: Record<string, unknown>
) {
  // dev-only logging removed
}

// ============================================
// Hook 옵션 및 반환 타입
// ============================================

/**
 * useSeatMap 훅 옵션
 */
export interface UseSeatMapOptions {
  /** 회사 코드 (KR/CN) */
  companyCode: CompanyCode;

  /** 스냅샷 종류 (기본: live) */
  variant?: SeatMapVariant;

  /** 자동 조회 활성화 (기본: true) */
  enabled?: boolean;
}

/**
 * useSeatMap 훅 반환 타입
 */
export interface UseSeatMapResult {
  /** 좌석배치도 데이터 */
  seatMap: SeatMapDocument | null;

  /** 로딩 상태 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 다시 조회 */
  refetch: () => Promise<void>;
}

// ============================================
// Hook 구현
// ============================================

/**
 * 좌석배치도 조회 Hook
 *
 * @param options 조회 옵션
 * @returns 좌석배치도 데이터 및 상태
 *
 * @example
 * ```typescript
 * function SeatMapPage() {
 *   const [variant, setVariant] = useState<SeatMapVariant>('live');
 *
 *   const { seatMap, isLoading, error, refetch } = useSeatMap({
 *     companyCode: 'KR',
 *     variant,
 *   });
 *
 *   if (isLoading) return <Loader />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <SeatMapCanvas seatMap={seatMap} />;
 * }
 * ```
 */
export function useSeatMap(options: UseSeatMapOptions): UseSeatMapResult {
  const { companyCode, variant = 'live', enabled = true } = options;

  const [seatMap, setSeatMap] = useState<SeatMapDocument | null>(null);
  // V53.2: enabled=true면 첫 렌더부터 isLoading=true로 시작 (빈 화면 깜빡임 방지)
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  // 마운트 상태 추적
  const isMountedRef = useRef(true);
  // V52.15: requestId 기반 stale fetch 차단 (숫자 ID로 정확한 비교)
  const requestIdRef = useRef(0);

  /**
   * V52.15: 데이터 조회 함수 (requestId 기반 stale 차단)
   */
  const fetchData = useCallback(async () => {
    if (!enabled) return;

    // V52.15: 고유 요청 ID 발급
    const reqId = ++requestIdRef.current;

    logFetch('start', { companyCode, variant, reqId });

    setIsLoading(true);
    setError(null);
    // V52.15: 에러 시에도 기존 seatMap 유지하려면 여기서 setSeatMap(null) 하지 않음

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<GetSeatMapRequest, GetSeatMapResponse>(
        functions,
        'getSeatMap'
      );

      const result = await fn({ companyCode, variant });

      // V52.15: stale 요청 차단 (reqId가 최신이 아니면 무시)
      if (!isMountedRef.current || requestIdRef.current !== reqId) {
        logFetch('stale_ignored', { companyCode, variant, reqId, currentReqId: requestIdRef.current });
        return;
      }

      if (result.data.success) {
        const fetchedSeatMap = result.data.seatMap;
        setSeatMap(fetchedSeatMap);

        logFetch('success', {
          companyCode,
          variant,
          reqId,
          hasSeatMap: !!fetchedSeatMap,
          objectsLen: fetchedSeatMap?.objects?.length ?? 0,
          canvasWidth: fetchedSeatMap?.canvasWidth,
          canvasHeight: fetchedSeatMap?.canvasHeight,
        });
      } else {
        throw new Error(result.data.error || '좌석배치도 조회 실패');
      }
    } catch (err: any) {
      // V52.15: stale 요청이면 에러도 무시
      if (!isMountedRef.current || requestIdRef.current !== reqId) {
        logFetch('stale_ignored', { companyCode, variant, reqId, currentReqId: requestIdRef.current, reason: 'error_path' });
        return;
      }

      const errorCode = err?.code || 'unknown';
      const errorMessage = err?.message || String(err);

      logFetch('error', { companyCode, variant, reqId, code: errorCode, message: errorMessage });

      setError(err);
      // V52.15: 에러 시 seatMap을 null로 덮어쓰지 않음 (기존 데이터 유지)
      // - 권한 에러/네트워크 에러 시 이전 화면 유지가 UX상 유리
      // - 단, "데이터가 정말 없음" 상태는 BE에서 success: true + seatMap: null로 내려옴
    } finally {
      // V52.15: stale 요청이면 loading도 false로 만들지 않음
      if (isMountedRef.current && requestIdRef.current === reqId) {
        setIsLoading(false);
      }
    }
  }, [companyCode, variant, enabled]);

  // V53.2: enabled 변경 시 isLoading 상태 동기화
  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      // enabled=true면 fetch 시작 전 로딩 상태 보장
      setIsLoading(true);
      fetchData();
    } else {
      // enabled=false면 로딩 상태 해제
      setIsLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData, enabled]);

  return {
    seatMap,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ============================================
// 편집 권한 확인 Hook
// ============================================

/**
 * useCanEditSeatMap 훅 옵션
 */
export interface UseCanEditSeatMapOptions {
  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 사용자 역할 ID 목록 */
  userRoleIds: string[];

  /** 사용자 회사 코드 */
  userCompanyCode?: CompanyCode;
}

/**
 * 좌석배치도 편집 권한 확인 Hook
 *
 * FE에서 UI 표시 용도로 권한을 판단합니다.
 * (실제 권한 검증은 BE에서 수행)
 *
 * @param options 권한 확인 옵션
 * @returns 편집 가능 여부
 *
 * @example
 * ```typescript
 * const canEdit = useCanEditSeatMap({
 *   companyCode: 'KR',
 *   userRoleIds: ['role_hr'],
 *   userCompanyCode: 'KR',
 * });
 *
 * if (canEdit) {
 *   // 편집 버튼 표시
 * }
 * ```
 */
export function useCanEditSeatMap(options: UseCanEditSeatMapOptions): boolean {
  const { companyCode, userRoleIds, userCompanyCode } = options;

  // system_admin은 모든 회사 편집 가능
  if (userRoleIds.includes('system_admin')) {
    return true;
  }

  // 회사별 admin
  if (companyCode === 'KR' && userRoleIds.includes('role_admin_kr')) {
    return true;
  }
  if (companyCode === 'CN' && userRoleIds.includes('role_admin_cn')) {
    return true;
  }

  // role_hr, role_manager, role_exec: 자기 회사만
  const hasEditorRole = ['role_hr', 'role_manager', 'role_exec'].some((r) =>
    userRoleIds.includes(r)
  );

  if (hasEditorRole && userCompanyCode === companyCode) {
    return true;
  }

  return false;
}
